import { Button, STYLE_REBRANDING_FILL } from '@eventbrite/eds-button';
import { ModalFooter, PaneLayoutInteractionType } from '@eventbrite/eds-modal';
import { GenericLazyString } from '@eventbrite/i18n';
import { $FixMe } from '@eventbrite/ts-utils';
import classNames from 'classnames';
import React, { FunctionComponent, ReactNode } from 'react';
import { BASE_URL, CHECKOUT_PATH } from '../../constants';
import { useDynamicStyles } from '../../utils/conditionalCssLoader';
import { getDisplayClasses } from '../../utils/displayClasses';
import ConnectedCartSummary from '../CartSummary';
import './PageFooter.scss';

const defaultStylesImporter = () => import('./PageFooter.module.scss');
const rebrandStylesImporter = () =>
    import('./PageFooter.rebranding.module.scss');

interface PropsRequiredForDefaultCtaButton {
    ctaType?:
        | 'none'
        | 'link'
        | 'danger'
        | 'neutral'
        | 'fill'
        | 'inverse'
        | 'anchor'
        | 'follow'
        | 'apple_pay';
    handleSubmit: (data?: { statsigValue: null | $FixMe }) => void;
    isSubmitDisabled: boolean;
    isSubmitting: boolean;
    keepDisabledStyleWhileLoading: boolean;
    routePathname: string;
}

type PropsRequiredForCartSummaryButton =
    | {
          isPaneOpen: false;
          onPaneToggle?: () => void;
          paneInteraction?: null;
      }
    | {
          isPaneOpen: boolean;
          onPaneToggle: () => void;
          paneInteraction: typeof PaneLayoutInteractionType;
      };

interface PropsOverridenByTertiaryNode {
    ctaNode: NonNullable<ReactNode>;
    modalFooterNode: NonNullable<ReactNode>;
    ctaText: GenericLazyString | string;
}

type NoneOf<T extends object> = Partial<Record<keyof T, null | undefined>>;

type CtaButtonProps =
    | ({
          ctaText?: null;
          modalFooterNode: PropsOverridenByTertiaryNode['modalFooterNode'];
      } & NoneOf<PropsRequiredForDefaultCtaButton>)
    | ({
          ctaText: PropsOverridenByTertiaryNode['ctaText'];
          modalFooterNode?: null;
      } & PropsRequiredForDefaultCtaButton);

type PropsForCartSummary =
    | ({
          showCartSummaryButton?: false;
      } & NoneOf<PropsRequiredForCartSummaryButton>)
    | ({
          showCartSummaryButton: true;
      } & PropsRequiredForCartSummaryButton);

export type PageFooterProps =
    | ({
          footerMessageNode?: null;
          shouldAlignLeft: boolean;
          tertiaryNode: ReactNode;
          urgencySignalNode?: null;
          isAppRebrandActive?: boolean;
          isCheckoutPage?: boolean;
          isExpressCheckout?: boolean;
          isSeriesInfiniteCalendar?: boolean;
      } & NoneOf<PropsOverridenByTertiaryNode> &
          NoneOf<CtaButtonProps> &
          NoneOf<PropsForCartSummary>)
    | ({
          footerMessageNode: ReactNode;
          tertiaryNode?: null;
          urgencySignalNode: ReactNode;
          isAppRebrandActive?: boolean;
          isCheckoutPage?: boolean;
          isExpressCheckout?: boolean;
          isSeriesInfiniteCalendar?: boolean;
      } & PropsForCartSummary &
          (
              | ({
                    ctaNode: PropsOverridenByTertiaryNode['ctaNode'];
                    shouldAlignLeft: boolean;
                    isAppRebrandActive?: boolean;
                } & NoneOf<CtaButtonProps>)
              | ({
                    ctaNode?: null;
                    shouldAlignLeft: boolean;
                    isAppRebrandActive?: boolean;
                } & CtaButtonProps)
              | ({
                    ctaNode?: null;
                    ctaText?: null;
                    shouldAlignLeft?: boolean;
                    isAppRebrandActive?: boolean;
                } & Partial<Omit<CtaButtonProps, 'ctaText'>>)
          ));

export const PageFooter: FunctionComponent<PageFooterProps> = ({
    ctaNode,
    ctaText,
    ctaType = 'fill',
    footerMessageNode,
    handleSubmit,
    isAppRebrandActive,
    isExpressCheckout,
    isPaneOpen,
    isSubmitDisabled,
    isSubmitting,
    isCheckoutPage = false,
    keepDisabledStyleWhileLoading,
    modalFooterNode,
    onPaneToggle,
    paneInteraction,
    shouldAlignLeft,
    showCartSummaryButton,
    isSeriesInfiniteCalendar,
    tertiaryNode,
    urgencySignalNode,
    routePathname,
}) => {
    const styles = useDynamicStyles({
        isAlternateStylesActive: isAppRebrandActive,
        defaultStylesImporter,
        alternateStylesImporter: rebrandStylesImporter,
    });

    // If there's nothing for modal footer just return
    if (!(modalFooterNode || ctaText || ctaNode || tertiaryNode)) {
        return null;
    }

    const buttonStyle = isAppRebrandActive ? STYLE_REBRANDING_FILL : ctaType;

    const defaultCtaButton = ctaText ? (
        <div
            className={classNames({
                'eds-align--left': shouldAlignLeft,
                'eds-align--right': !shouldAlignLeft,
            })}
        >
            <Button
                onClick={handleSubmit}
                __containerClassName={classNames(
                    isSeriesInfiniteCalendar &&
                        styles.modalFooterButtonContainer,
                    {
                        [styles.modalFooterScrollableOnDesktop]:
                            shouldAlignLeft,
                    },
                    styles.singleCtaButton,
                    { [styles.onCheckoutPage]: isCheckoutPage },
                )}
                style={buttonStyle}
                disabled={isSubmitDisabled}
                keepDisabledStyleWhileLoading={keepDisabledStyleWhileLoading}
                data-spec="eds-modal__primary-button"
                data-automation="eds-modal__primary-button"
                data-testid="eds-modal__primary-button"
                isLoading={isSubmitting}
                isFullWidth={true}
            >
                {ctaText}
            </Button>
        </div>
    ) : null;

    const actionNode = modalFooterNode || defaultCtaButton;

    const cartSummaryNode = showCartSummaryButton ? (
        <ConnectedCartSummary
            isPaneOpen={isPaneOpen}
            onClick={onPaneToggle}
            paneInteraction={paneInteraction}
            isAppRebrandActive={isAppRebrandActive}
            isSeriesInfiniteCalendarViewActive={isSeriesInfiniteCalendar}
            isExpressCheckout={isExpressCheckout}
        />
    ) : null;

    const ctaNodeWithWrapper = ctaNode ? (
        <div
            data-testid="cta-node-wrapper"
            className={classNames({
                [styles.ctaNodeWrapperAlignRight]: !shouldAlignLeft,
            })}
        >
            {ctaNode}
        </div>
    ) : null;

    const contentNode = tertiaryNode || (
        <>
            {urgencySignalNode}
            {footerMessageNode}
            {cartSummaryNode}
            {ctaNodeWithWrapper}
        </>
    );

    const footerContainerClassName = classNames(
        getDisplayClasses({ shouldAlignLeft }),
        styles.modalFooter,
        {
            'modal-footer-cta-node': ctaNode,
            [styles.modalFooterNoLeftMargin]: shouldAlignLeft,
            'checkout-modal-footer--no-top-border': shouldAlignLeft,
        },
    );

    const footerOuterContainerClassName = classNames(styles.wrapper, {
        [styles.wrapperNoBorder]: isPaneOpen && isAppRebrandActive,
        [styles.checkoutFormButtonInfiniteCalendarSeries]:
            isSeriesInfiniteCalendar &&
            routePathname === `${BASE_URL}/${CHECKOUT_PATH}`,
        [styles.checkoutFormButton]: !isSeriesInfiniteCalendar,
        [styles.pageFooterButton]: !isCheckoutPage && !isSeriesInfiniteCalendar,
    });

    return (
        <ModalFooter
            __footerOuterContainerClassName={footerOuterContainerClassName}
            __footerContainerClassName={footerContainerClassName}
        >
            <div
                className={classNames(styles.modalFooterWrapper, {
                    seriesInfiniteCalendarWrapper: isSeriesInfiniteCalendar,
                })}
            >
                {contentNode}
                {actionNode}
            </div>
        </ModalFooter>
    );
};
